// §6.3 — this skin's `Bar` is **two** bars: the menu bar at the top and the Dock at the bottom.
//
// §6.1's component list is closed and has one `Bar` slot, so both live behind it. That is not a
// workaround: they are the same thing the other skins' single bar is — the desktop's own chrome —
// and the shell already renders `Bar` in a layer above the windows.
//
// The layer is positioned by `shell.css` at the *bottom*, so the menu bar takes itself back to the
// top with `position: fixed` in this skin's own sheet. Only the dock's height is `--bar-height`,
// because that is the token the shell subtracts from the work area at the bottom (§5.4, D-144).
import { useEffect, useState } from 'react';
import { COPY } from '@/copy';
import {
  useFocusedWindowId,
  useKernel,
  useNotificationBadges,
  useUnhealthyApps,
  useWindows,
} from '@/kernel/react';
import { GlobalMenuBar } from '../../menubar';
import { Dock } from './Dock';

function useClock(): string {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 10_000);
    return () => clearInterval(timer);
  }, []);
  // The platform's format: weekday, date, time, all in one line at the right of the menu bar.
  return now.toLocaleString([], {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function Bar() {
  const kernel = useKernel();
  const windows = useWindows();
  const focusedId = useFocusedWindowId();
  const unhealthy = useUnhealthyApps();
  const badges = useNotificationBadges();
  const time = useClock();

  return (
    <>
      <div className="macos-menubar" data-testid="bar">
        {/* §6.3 / §9.3 — the focused window's published tree, in a bar that outlives the window. */}
        <GlobalMenuBar />

        <div className="macos-menubar-status" data-testid="bar-tray">
          <button
            type="button"
            className="macos-menubar-item"
            data-testid="bar-spotlight"
            aria-label={COPY.chrome.openLauncher}
            onClick={() => kernel.shellui.toggleOverlay('launcher')}
          >
            ⌕
          </button>
          {/* §13 — the Center hangs off the clock here, as it does on the platform. */}
          <button
            type="button"
            className="macos-menubar-item macos-menubar-clock"
            data-testid="bar-clock"
            aria-label={COPY.chrome.notifications}
            onClick={() => kernel.shellui.toggleOverlay('notifications')}
          >
            {time}
          </button>
        </div>
      </div>

      <Dock windows={windows} focusedId={focusedId} unhealthy={unhealthy} badges={badges} />
    </>
  );
}
